import { useMemo } from 'react';
import { userSelector } from 'slices/auth.slice';
import { useAppSelector } from 'slices/store';
import { EAppId, EAuthStrategy } from '@timeedit/types/lib/enums';
import type { IAuthConfig, ISAML2Config } from '@timeedit/types/lib/types';
// TYPES
import { hasPermissions } from '@timeedit/types/lib/utils/permissionCalculator';
import { TEInput, Typography } from '@timeedit/ui-components';
// STYLES
import { Checkbox, Form, Input, Select } from '@timeedit/ui-components/antd';
import { Flex } from '@timeedit/ui-components/antd';
import SAML2Config from '../../../components/AuthConfigs/SAML2Config';
import intl from '../../../i18n/intl';
// COMPONENTS
import { useGetAuthConfigsQuery } from './api/authConfig.api';

const language: any = intl.messages;

type Props = {
  authConfig: IAuthConfig;
  originalAuthConfig?: IAuthConfig;
  onChange: (prop: string, value: any) => void;
};

const AuthConfigDetailPage = ({
  authConfig,
  originalAuthConfig,
  onChange,
}: Props) => {
  const [form] = Form.useForm();
  const { data: authConfigs = [] } = useGetAuthConfigsQuery();
  const authUser = useAppSelector(userSelector);

  const isRootUser = useMemo(
    () => authUser?.scopes?.includes('TE_ROOT_USER'),
    [authUser]
  );

  const hasEditPermission = useMemo(
    () =>
      hasPermissions(authUser?.appPermissions, authUser?.scopes, {
        permissions: ['TE_ADMIN::manageAuthConfigs'],
      }),
    [authUser]
  );

  const isDisabled = (isDisabled: boolean) => {
    return !hasEditPermission || isDisabled;
  };

  return (
    <>
      <Form layout="vertical" form={form} disabled={!hasEditPermission}>
        <AuthConfigTitle title={language.general_settings} />
        <Flex className="authConfig-container" vertical gap="small">
          <TEInput.LabelWrapper label={`${language.name}`}>
            <Input
              size="small"
              value={authConfig.name || undefined}
              onChange={(event) => onChange('name', event.target.value)}
            />
          </TEInput.LabelWrapper>
          <TEInput.LabelWrapper label={`${language.id}`}>
            <Typography.Text>{authConfig.id}</Typography.Text>
          </TEInput.LabelWrapper>
          <TEInput.LabelWrapper
            label={language.auth_config_active}
            tooltip={language.help_auth_config_active}
          >
            <Checkbox
              checked={authConfig.active}
              onChange={(e) => onChange('active', e.target.checked)}
            />
          </TEInput.LabelWrapper>
          <TEInput.LabelWrapper
            label={language.auth_config_default}
            tooltip={language.help_auth_config_default}
          >
            <Checkbox
              checked={authConfig.default}
              onChange={(e) => onChange('default', e.target.checked)}
              disabled={!authConfig.active}
            />
          </TEInput.LabelWrapper>
          <TEInput.LabelWrapper
            tooltip={language.help_enabled_applications}
            label={language.enabled_applications}
          >
            <Select
              mode="multiple"
              showSearch
              disabled={!isRootUser}
              size="small"
              filterOption={(input: any, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              value={authConfig.enabledForAppIds}
              onChange={(enabledForAppIds: string[]) =>
                onChange('enabledForAppIds', enabledForAppIds)
              }
            >
              {Object.entries(EAppId).map(([key, value]) => (
                <Select.Option value={value} key={key}>
                  {value}
                </Select.Option>
              ))}
            </Select>
          </TEInput.LabelWrapper>
          <TEInput.LabelWrapper
            label={language.strategy}
            tooltip={language.help_auth_configs_strategy}
          >
            <Select
              size="small"
              showSearch
              disabled={isDisabled(
                authConfig.strategy === EAuthStrategy.TE_IDP
              )}
              filterOption={(input: any, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              value={authConfig.strategy}
              onChange={(value) => onChange('strategy', value)}
            >
              {Object.entries(EAuthStrategy).map(([key, value]) => (
                <Select.Option
                  value={value}
                  key={key}
                  disabled={isDisabled(
                    value === EAuthStrategy.TE_IDP &&
                      authConfigs.filter(
                        (authConfig) =>
                          authConfig.strategy === EAuthStrategy.TE_IDP
                      ).length > 0
                  )}
                >
                  {value}
                </Select.Option>
              ))}
            </Select>
          </TEInput.LabelWrapper>
          <TEInput.LabelWrapper
            label={`${language.description}`}
            tooltip={language.description}
          >
            <Input.TextArea
              size="small"
              value={authConfig.description || undefined}
              onChange={(event: any) =>
                onChange('description', event.target.value)
              }
            />
          </TEInput.LabelWrapper>
        </Flex>
        {authConfig.strategy !== EAuthStrategy.TE_IDP && (
          <>
            {/* {authConfig.strategy === EAuthStrategy.TE_IDP && (
                <TEIDPConfig onChange={onChange} config={authConfig.config as ITEIdPConfig} />
              )} */}
            {/* <Form.Item label={language.log_level} tooltip={language.help_log_level}>
                <Select
                  showSearch
                  style={{ width: 100 }}
                  size='small'
                  filterOption={(input: string, option: any) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  value={authConfig.config.logLevel}
                  onChange={(logLevel) => onChange('config', {...authConfig.config, logLevel})}
                >
                  {Object.entries(EAuthLogLevel).map(([key, value]) => (
                    <Select.Option value={value} key={key}>
                      {language[value]}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item> */}
            {authConfig.strategy === EAuthStrategy.SAML2 && authConfig.id && (
              <>
                <AuthConfigTitle title={language.configuration} />
                <SAML2Config
                  onChange={onChange}
                  authConfigId={authConfig.id}
                  config={authConfig.config as ISAML2Config}
                  originalConfig={originalAuthConfig?.config as ISAML2Config}
                  hasEditPermission={hasEditPermission}
                />
              </>
            )}
          </>
        )}
      </Form>
    </>
  );
};

const AuthConfigTitle = ({ title }: { title: string }) => (
  <Typography.Title level={4} className="authConfig-title">
    {title}
  </Typography.Title>
);

export default AuthConfigDetailPage;
